import type { Transaction } from "@rocicorp/zero";
import { type Context, userFacingError } from "../authorization";
import { dropUsagesLostByGrantRemoval } from "../connectorUsages";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { assertNotPersonal } from "../spaceName";
import { hasAccess } from "./access";
import { grantsForScope } from "./grantQueries";
import type { Permission, ShareableScopeKind } from "./permissions";
import { BUILTIN_ROLES, type BuiltinRoleId } from "./roles";
import {
  applyGrantDiff,
  assertCustomRoleScope,
  assertDependentPrincipalsMeetRequirements,
  dedupGrants,
  diffGrants,
  type GrantInput,
  principalsCrossingIntoRequirement,
  validateGrantsInput,
} from "./validation";

// The core of setScopeRoleGrants: validate, diff against the current grants, run
// the cross-resource-availability rules, and apply. Extracted so approving a
// grant request runs the exact same logic as sharing directly — the only
// difference is who is authorized to trigger it. The escalation guard below
// checks the acting principal (`ctx.principal`, e.g. a service account, falling
// back to `ctx.userId`), which is the reviewer when called from an approval.
export async function applyScopeRoleGrants(opts: {
  tx: Transaction<Schema>;
  ctx: Context;
  scopeId: string;
  scopeKind: ShareableScopeKind;
  grants: readonly GrantInput[];
}): Promise<void> {
  const { tx, ctx, scopeId, scopeKind, grants } = opts;

  // The scope id is caller-supplied and looked up unscoped (no RLS), so filter
  // by tenant: a foreign scope must read as "not found", never be grantable.
  if (scopeKind === "space") {
    const [space] = await tx.run(
      zql.spaces.where("id", scopeId).where("tenantId", ctx.tenantId)
    );
    if (!space) throw userFacingError("Space not found");
    assertNotPersonal(space, "set grants on");
  } else if (scopeKind === "skill") {
    const [skill] = await tx.run(
      zql.skills.where("id", scopeId).where("tenantId", ctx.tenantId)
    );
    if (!skill) throw userFacingError("Skill not found");
  } else if (scopeKind === "network") {
    const [network] = await tx.run(
      zql.networks.where("id", scopeId).where("tenantId", ctx.tenantId)
    );
    if (!network) throw userFacingError("Network not found");
  } else {
    const [app] = await tx.run(
      zql.connectedApps.where("id", scopeId).where("tenantId", ctx.tenantId)
    );
    if (!app) throw userFacingError("Connector not found");
  }

  const deduped = dedupGrants(grants);

  validateGrantsInput(deduped, scopeKind);

  const customRoleMap = new Map<
    string,
    { rolePermissions?: ReadonlyArray<{ permission: string }> }
  >();

  for (const grant of deduped) {
    if (grant.customRoleId) {
      const [customRole] = await tx.run(
        zql.roles
          .where("id", grant.customRoleId)
          .where("tenantId", ctx.tenantId)
          .related("rolePermissions")
      );
      if (!customRole) throw userFacingError("Custom role not found");
      assertCustomRoleScope(customRole, scopeKind);
      customRoleMap.set(grant.customRoleId, customRole);
    }

    if (grant.serviceAccountId) {
      const [sa] = await tx.run(
        zql.serviceAccounts
          .where("id", grant.serviceAccountId)
          .where("tenantId", ctx.tenantId)
      );
      if (!sa) throw userFacingError("Service account not found");

      // Escalation guard, service-account principals only: granting an SA a
      // role lets anyone who can assume that account act with these
      // permissions, so the granter must already hold each conferred
      // permission on this scope. Users/groups are not guarded -- you cannot
      // become a user or group you grant. Tenant admins pass via allow_all.
      let conferred: readonly Permission[];
      if (grant.builtinRoleId) {
        conferred =
          BUILTIN_ROLES[grant.builtinRoleId as BuiltinRoleId]?.permissions ??
          [];
      } else {
        const customRole = customRoleMap.get(grant.customRoleId!);
        conferred = (customRole?.rolePermissions?.map((rp) => rp.permission) ??
          []) as Permission[];
      }
      for (const permission of conferred) {
        if (permission === "allow_all") {
          throw userFacingError(
            "Cannot grant a service account a role carrying allow_all"
          );
        }
        const granterHas = await hasAccess({
          tx,
          userId: ctx.userId,
          // Check the acting principal (`ctx.principal`, e.g. a service
          // account): a service account granter's userId is its own
          // service-account id, so without this it is validated as a bogus
          // user and holds nothing.
          principal: ctx.principal,
          scopeId,
          tenantId: ctx.tenantId,
          permission,
        });
        if (!granterHas) {
          throw userFacingError(
            `You cannot grant "${permission}" to a service account because you do not hold it on this scope`
          );
        }
      }
    }
  }

  const allExisting = await tx.run(grantsForScope(scopeId, ctx.tenantId));
  const existing = allExisting.filter(
    (g) => g.userGroupId || g.userId || g.serviceAccountId
  );

  const diff = diffGrants(existing, deduped);

  if (scopeKind === "space") {
    const desiredPrincipals = deduped.map((g) => ({
      userGroupId: g.userGroupId ?? null,
      userId: g.userId ?? null,
      serviceAccountId: g.serviceAccountId ?? null,
      builtinRoleId: g.builtinRoleId ?? null,
      customRole: g.customRoleId ? customRoleMap.get(g.customRoleId) : null,
    }));
    for (const ruleId of [
      "space_uses_connected_apps",
      "space_uses_networks",
      "space_uses_skills",
    ] as const) {
      const elevated = principalsCrossingIntoRequirement(
        ruleId,
        existing,
        desiredPrincipals
      );
      await assertDependentPrincipalsMeetRequirements(
        tx,
        ctx.tenantId,
        ruleId,
        scopeId,
        elevated
      );
    }
  }

  await applyGrantDiff(tx, scopeId, diff, ctx.tenantId);

  // Skills need no removal handling: a space "uses" a skill through grant
  // overlap alone, so revoking a grant leaves nothing behind to disconnect.
  if (scopeKind === "connected_app") {
    await dropUsagesLostByGrantRemoval(
      tx,
      ctx.tenantId,
      scopeId,
      diff.removedGroupIds,
      diff.removedUserIds,
      diff.removedServiceAccountIds
    );
  }
}
